import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Button,
    Card,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    Pagination,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from "antd";

import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    SearchOutlined,
    SwapOutlined,
} from "@ant-design/icons";

import Swal from "sweetalert2";

import { pdf } from "@react-pdf/renderer";

import "./TeacherDataForm.css";

import api from "../../api/api";

import { useAcademicYear } from "../../AcademicYearContext";

import TeacherDataPDF from "./TeacherDataPDF";

const {
    Title,
    Text,
} = Typography;

const TeacherDataForm = () => {
    const [form] = Form.useForm();

    const {
        selectedYear,
        academicYears = [],
    } = useAcademicYear();

    const [open, setOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    const [tableLoading, setTableLoading] = useState(false);

    const [transferLoading, setTransferLoading] = useState(false);

    const [teachers, setTeachers] = useState([]);

    const [users, setUsers] = useState([]);

    const [divisions, setDivisions] = useState([]);

    const [subjects, setSubjects] = useState([]);

    const [gradeClasses, setGradeClasses] = useState([]);

    const [editingId, setEditingId] = useState(null);

    const [editingTeacher, setEditingTeacher] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");

    const [currentPage, setCurrentPage] = useState(1);

    const [pageSize, setPageSize] = useState(8);

    const [messageApi, contextHolder] =
        message.useMessage();

    useEffect(() => {
        fetchUsers();
        fetchDivisions();
        fetchSubjects();
        fetchGradeClasses();
    }, []);

    useEffect(() => {
        if (selectedYear?.id) {
            fetchTeachers();
        } else {
            setTeachers([]);
        }

        setCurrentPage(1);
    }, [selectedYear?.id]);

    const fetchTeachers = async () => {
        if (!selectedYear?.id) {
            return;
        }

        try {
            setTableLoading(true);

            const response = await api.get(
                "/api/users/teacher-data/",
                {
                    params: {
                        academic_year:
                            selectedYear.id,
                    },
                }
            );

            const data =
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || [];

            setTeachers(data);
        } catch (error) {
            console.error(
                "FETCH TEACHERS ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load teacher data."
            );
        } finally {
            setTableLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await api.get(
                "/api/teacherslist/"
            );

            setUsers(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
            );
        } catch (error) {
            console.error(
                "FETCH USERS ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load users."
            );
        }
    };

    const fetchDivisions = async () => {
        try {
            const response = await api.get(
                "/api/users/divisions/"
            );

            setDivisions(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
            );
        } catch (error) {
            console.error(
                "FETCH DIVISIONS ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load divisions."
            );
        }
    };

    const fetchSubjects = async () => {
        try {
            const response = await api.get(
                "/api/users/subjects/"
            );

            setSubjects(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
            );
        } catch (error) {
            console.error(
                "FETCH SUBJECTS ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load subjects."
            );
        }
    };

    const fetchGradeClasses = async () => {
        try {
            const response = await api.get(
                "/api/users/grade-classes/"
            );

            setGradeClasses(
                Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
            );
        } catch (error) {
            console.error(
                "FETCH GRADE CLASSES ERROR:",
                error.response?.data || error
            );

            messageApi.error(
                "Unable to load grade classes."
            );
        }
    };

    const filteredTeachers = useMemo(() => {
        const search =
            searchTerm
                .toLowerCase()
                .trim();

        if (!search) {
            return teachers;
        }

        return teachers.filter(
            (teacher) =>
                teacher.full_name
                    ?.toLowerCase()
                    .includes(search) ||
                teacher.username?.username
                    ?.toLowerCase()
                    .includes(search) ||
                teacher.username?.toString()
                    ?.toLowerCase()
                    .includes(search) ||
                teacher.employment_status_display
                    ?.toLowerCase()
                    .includes(search) ||
                teacher.employment_status
                    ?.toLowerCase()
                    .includes(search)
        );
    }, [
        teachers,
        searchTerm,
    ]);

    const getDivisionNames = (
        ids = []
    ) => {
        if (!Array.isArray(ids)) {
            return [];
        }

        return ids
            .map(
                (id) =>
                    divisions.find(
                        (division) =>
                            division.id === id
                    )?.name
            )
            .filter(Boolean);
    };

    const getSubjectNames = (
        ids = []
    ) => {
        if (!Array.isArray(ids)) {
            return "";
        }

        return ids
            .map(
                (id) =>
                    subjects.find(
                        (subject) =>
                            subject.id === id
                    )?.name
            )
            .filter(Boolean)
            .join(", ");
    };

    const getGradeNames = (
        ids = []
    ) => {
        if (!Array.isArray(ids)) {
            return "";
        }

        return ids
            .map(
                (id) =>
                    gradeClasses.find(
                        (grade) =>
                            grade.id === id
                    )?.name
            )
            .filter(Boolean)
            .join(", ");
    };

    const getEmploymentStatus = (
        teacher
    ) => {
        return (
            teacher?.employment_status ||
            "active"
        );
    };

    const getEmploymentStatusDisplay = (
        teacher
    ) => {
        const status =
            getEmploymentStatus(
                teacher
            );

        return (
            teacher?.employment_status_display ||
            (
                status === "fired"
                    ? "Sacked / Fired"
                    : status
                        .charAt(0)
                        .toUpperCase() +
                      status.slice(1)
            )
        );
    };

    const getStatusTagColor = (
        status
    ) => {
        switch (status) {
            case "active":
                return "green";

            case "removed":
                return "orange";

            case "fired":
                return "red";

            default:
                return "default";
        }
    };

    const getUsernameId = (
        teacher
    ) => {
        if (
            teacher?.username &&
            typeof teacher.username === "object"
        ) {
            return teacher.username.id;
        }

        return teacher?.username;
    };

    const resetForm = () => {
        form.resetFields();

        setEditingId(null);

        setEditingTeacher(null);
    };

    const closeModal = () => {
        setOpen(false);

        resetForm();
    };

    const openAddModal = () => {
        resetForm();

        form.setFieldsValue({
            employment_status:
                "active",
            status_reason:
                "",
            divisions:
                [],
            subjects:
                [],
            grade_class:
                [],
        });

        setOpen(true);
    };

    const handleEdit = (
        teacher
    ) => {
        const teacherDivisions =
            Array.isArray(
                teacher.divisions
            )
                ? teacher.divisions
                : teacher.division
                    ? [teacher.division]
                    : [];

        const employmentStatus =
            teacher.employment_status ||
            "active";

        form.setFieldsValue({
            username:
                getUsernameId(
                    teacher
                ),

            full_name:
                teacher.full_name || "",

            qualification:
                teacher.qualification || "",

            divisions:
                teacherDivisions,

            dependent_student:
                teacher.dependent_student || "",

            contact:
                teacher.contact || "",

            salary:
                teacher.salary ?? null,

            date_add:
                teacher.date_add || "",

            subjects:
                Array.isArray(
                    teacher.subjects
                )
                    ? teacher.subjects
                    : [],

            grade_class:
                Array.isArray(
                    teacher.grade_class
                )
                    ? teacher.grade_class
                    : [],

            employment_status:
                employmentStatus,

            status_reason:
                teacher.status_reason || "",
        });

        setEditingId(
            teacher.id
        );

        setEditingTeacher(
            teacher
        );

        setOpen(true);
    };

    const getNextAcademicYear = () => {
        if (
            !selectedYear?.id ||
            !Array.isArray(academicYears) ||
            !academicYears.length
        ) {
            return null;
        }

        const currentIndex =
            academicYears.findIndex(
                (year) =>
                    Number(year.id) ===
                    Number(selectedYear.id)
            );

        if (
            currentIndex === -1
        ) {
            return null;
        }

        const currentStartYear =
            parseInt(
                String(
                    selectedYear.name || ""
                ).match(
                    /\d{4}/
                )?.[0] || "",
                10
            );

        if (
            Number.isNaN(
                currentStartYear
            )
        ) {
            return (
                academicYears[
                    currentIndex + 1
                ] || null
            );
        }

        const possibleNextYears =
            academicYears
                .filter(
                    (year) =>
                        Number(year.id) !==
                        Number(selectedYear.id)
                )
                .map(
                    (year) => ({
                        year,
                        startYear:
                            parseInt(
                                String(
                                    year.name || ""
                                ).match(
                                    /\d{4}/
                                )?.[0] || "",
                                10
                            ),
                    })
                )
                .filter(
                    (item) =>
                        !Number.isNaN(
                            item.startYear
                        ) &&
                        item.startYear >
                            currentStartYear
                )
                .sort(
                    (a, b) =>
                        a.startYear -
                        b.startYear
                );

        return (
            possibleNextYears[0]
                ?.year || null
        );
    };

    const downloadTeacherPDF = async (
        teacher
    ) => {
        try {
            const divisionIds =
                Array.isArray(
                    teacher.divisions
                )
                    ? teacher.divisions
                    : teacher.division
                        ? [teacher.division]
                        : [];

            const divisionNames =
                Array.isArray(
                    teacher.division_names
                )
                    ? teacher.division_names
                    : getDivisionNames(
                        divisionIds
                    );

            const blob =
                await pdf(
                    <TeacherDataPDF
                        teacher={
                            teacher
                        }
                        divisionNames={
                            divisionNames
                        }
                        divisionName={
                            divisionNames.join(
                                ", "
                            )
                        }
                        subjectNames={
                            getSubjectNames(
                                teacher.subjects
                            )
                        }
                        gradeNames={
                            getGradeNames(
                                teacher.grade_class
                            )
                        }
                    />
                ).toBlob();

            const url =
                URL.createObjectURL(
                    blob
                );

            const link =
                document.createElement(
                    "a"
                );

            link.href = url;

            link.download =
                `${teacher.full_name}_Teacher_Data.pdf`;

            document.body.appendChild(
                link
            );

            link.click();

            document.body.removeChild(
                link
            );

            URL.revokeObjectURL(
                url
            );
        } catch (error) {
            console.error(
                "PDF ERROR:",
                error
            );

            messageApi.error(
                "Unable to generate teacher PDF."
            );
        }
    };

    const handleSubmit = async (
        values
    ) => {
        if (!selectedYear?.id) {
            messageApi.error(
                "Please select an academic year first."
            );

            return;
        }

        const employmentStatus =
            values.employment_status ||
            "active";

        const statusReason =
            (
                values.status_reason ||
                ""
            ).trim();

        if (
            (
                employmentStatus ===
                    "removed" ||
                employmentStatus ===
                    "fired"
            ) &&
            !statusReason
        ) {
            messageApi.error(
                "Please provide a reason for the employment status."
            );

            return;
        }

        if (
            editingId &&
            (
                employmentStatus ===
                    "removed" ||
                employmentStatus ===
                    "fired"
            )
        ) {
            const statusLabel =
                employmentStatus ===
                    "fired"
                    ? "SACKED / FIRED"
                    : "REMOVED";

            const result =
                await Swal.fire({
                    title:
                        `Set Teacher as ${statusLabel}?`,

                    html: `
                        <div style="text-align:left;">
                            <p>
                                This status applies only to:
                            </p>

                            <p>
                                <strong>
                                    ${selectedYear.name}
                                </strong>
                            </p>

                            <p>
                                The teacher's historical academic-year
                                record will be preserved.
                            </p>

                            <p>
                                Login will be blocked for this
                                academic year.
                            </p>
                        </div>
                    `,

                    icon:
                        "warning",

                    showCancelButton:
                        true,

                    confirmButtonText:
                        "Yes, Continue",

                    cancelButtonText:
                        "Cancel",

                    confirmButtonColor:
                        "#d9363e",

                    cancelButtonColor:
                        "#111111",
                });

            if (
                !result.isConfirmed
            ) {
                return;
            }
        }

        const wasEditing =
            Boolean(
                editingId
            );

        try {
            setLoading(true);

            const payload = {
                ...values,

                academic_year:
                    selectedYear.id,

                employment_status:
                    employmentStatus,

                status_reason:
                    statusReason,

                divisions:
                    Array.isArray(
                        values.divisions
                    )
                        ? values.divisions
                        : [],

                subjects:
                    Array.isArray(
                        values.subjects
                    )
                        ? values.subjects
                        : [],

                grade_class:
                    Array.isArray(
                        values.grade_class
                    )
                        ? values.grade_class
                        : [],
            };

            let response;

            if (editingId) {
                response =
                    await api.put(
                        `/api/users/teacher-data/${editingId}/`,
                        payload
                    );
            } else {
                response =
                    await api.post(
                        "/api/users/teacher-data/",
                        payload
                    );
            }

            const teacherId =
                response.data?.id ||
                editingId;

            if (!teacherId) {
                throw new Error(
                    "The server did not return the teacher record ID."
                );
            }

            const teacherResponse =
                await api.get(
                    `/api/users/teacher-data/${teacherId}/`
                );

            const savedTeacher =
                teacherResponse.data;

            await fetchTeachers();

            closeModal();

            const savedStatus =
                savedTeacher.employment_status ||
                employmentStatus;

            let successTitle =
                wasEditing
                    ? "Teacher Updated"
                    : "Teacher Added";

            let successText =
                "Teacher data was saved successfully.";

            if (
                savedStatus ===
                "removed"
            ) {
                successTitle =
                    "Teacher Marked Removed";

                successText =
                    `The teacher is now marked as REMOVED for ${selectedYear.name}. Login is blocked for this academic year.`;
            }

            if (
                savedStatus ===
                "fired"
            ) {
                successTitle =
                    "Teacher Marked Sacked / Fired";

                successText =
                    `The teacher is now marked as SACKED / FIRED for ${selectedYear.name}. Login is blocked for this academic year.`;
            }

            await Swal.fire({
                icon:
                    "success",

                title:
                    successTitle,

                text:
                    successText,

                confirmButtonColor:
                    "#ff7900",
            });

            await downloadTeacherPDF(
                savedTeacher
            );
        } catch (error) {
            console.error(
                "SAVE TEACHER ERROR:",
                error.response?.data ||
                    error
            );

            const backendError =
                error.response?.data;

            let errorText =
                "Something went wrong.";

            if (
                backendError?.detail
            ) {
                errorText =
                    backendError.detail;
            } else if (
                backendError &&
                typeof backendError ===
                    "object"
            ) {
                errorText =
                    Object.entries(
                        backendError
                    )
                        .map(
                            (
                                [
                                    field,
                                    errors,
                                ]
                            ) =>
                                `${field}: ${
                                    Array.isArray(
                                        errors
                                    )
                                        ? errors.join(
                                              ", "
                                          )
                                        : errors
                                }`
                        )
                        .join("\n");
            }

            await Swal.fire({
                icon:
                    "error",

                title:
                    "Operation Failed",

                text:
                    errorText,

                confirmButtonColor:
                    "#111111",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleTransferToNextYear =
        async (
            teacher
        ) => {
            if (!teacher) {
                return;
            }

            if (!selectedYear?.id) {
                messageApi.error(
                    "Please select the current academic year first."
                );

                return;
            }

            const nextYear =
                getNextAcademicYear();

            if (!nextYear) {
                await Swal.fire({
                    icon:
                        "warning",

                    title:
                        "Next Academic Year Not Found",

                    text:
                        "There is no next academic year available in the Academic Year list.",
                    
                    confirmButtonColor:
                        "#ff7900",
                });

                return;
            }

            const status =
                getEmploymentStatus(
                    teacher
                );

            if (
                status !==
                "active"
            ) {
                await Swal.fire({
                    icon:
                        "warning",

                    title:
                        "Teacher Cannot Be Transferred",

                    html: `
                        <div style="text-align:left;">
                            <p>
                                <strong>
                                    ${teacher.full_name || "This teacher"}
                                </strong>
                                is currently marked as
                                <strong>
                                    ${getEmploymentStatusDisplay(
                                        teacher
                                    )}
                                </strong>
                                for
                                <strong>
                                    ${selectedYear.name}
                                </strong>.
                            </p>

                            <p>
                                Only an active teacher can be
                                transferred to the next academic year.
                            </p>
                        </div>
                    `,

                    confirmButtonColor:
                        "#ff7900",
                });

                return;
            }

            const existingTeacher =
                teachers.find(
                    (item) =>
                        Number(
                            getUsernameId(
                                item
                            )
                        ) ===
                        Number(
                            getUsernameId(
                                teacher
                            )
                        )
                );

            if (
                existingTeacher &&
                Number(
                    existingTeacher.id
                ) !==
                    Number(
                        teacher.id
                    )
            ) {
                return;
            }

            const result =
                await Swal.fire({
                    icon:
                        "question",

                    title:
                        "Transfer Teacher?",

                    html: `
                        <div style="text-align:left;line-height:1.7;">
                            <p>
                                You are about to create a new
                                academic-year teacher record for:
                            </p>

                            <p>
                                <strong>
                                    ${teacher.full_name || "Teacher"}
                                </strong>
                            </p>

                            <p>
                                From:
                                <strong>
                                    ${selectedYear.name}
                                </strong>
                            </p>

                            <p>
                                To:
                                <strong>
                                    ${nextYear.name}
                                </strong>
                            </p>

                            <hr />

                            <p>
                                The current
                                <strong>
                                    ${selectedYear.name}
                                </strong>
                                record will NOT be changed or deleted.
                            </p>

                            <p>
                                Subjects, grade classes, divisions,
                                qualification, contact and salary
                                assignments will be copied to the new
                                academic-year record.
                            </p>

                            <p>
                                The new
                                <strong>
                                    ${nextYear.name}
                                </strong>
                                record will be created as
                                <strong>
                                    ACTIVE
                                </strong>.
                            </p>
                        </div>
                    `,

                    showCancelButton:
                        true,

                    confirmButtonText:
                        `Transfer to ${nextYear.name}`,

                    cancelButtonText:
                        "Cancel",

                    confirmButtonColor:
                        "#ff7900",

                    cancelButtonColor:
                        "#111111",

                    width:
                        600,
                });

            if (
                !result.isConfirmed
            ) {
                return;
            }

            try {
                setTransferLoading(
                    true
                );

                const usernameId =
                    getUsernameId(
                        teacher
                    );

                if (!usernameId) {
                    throw new Error(
                        "Unable to determine the teacher username."
                    );
                }

                const divisionIds =
                    Array.isArray(
                        teacher.divisions
                    )
                        ? teacher.divisions
                        : teacher.division
                            ? [
                                  teacher.division,
                              ]
                            : [];

                const subjectIds =
                    Array.isArray(
                        teacher.subjects
                    )
                        ? teacher.subjects
                        : [];

                const gradeClassIds =
                    Array.isArray(
                        teacher.grade_class
                    )
                        ? teacher.grade_class
                        : [];

                const transferPayload =
                    {
                        username:
                            usernameId,

                        full_name:
                            teacher.full_name ||
                            "",

                        qualification:
                            teacher.qualification ||
                            "",

                        divisions:
                            divisionIds,

                        contact:
                            teacher.contact ||
                            "",

                        salary:
                            teacher.salary ??
                            null,

                        date_add:
                            teacher.date_add ||
                            "",

                        dependent_student:
                            teacher.dependent_student ||
                            "",

                        subjects:
                            subjectIds,

                        grade_class:
                            gradeClassIds,

                        academic_year:
                            nextYear.id,

                        employment_status:
                            "active",

                        status_reason:
                            "",
                    };

                const transferResponse =
                    await api.post(
                        "/api/users/teacher-data/",
                        transferPayload
                    );

                const newTeacherId =
                    transferResponse.data?.id;

                let newTeacher =
                    transferResponse.data;

                if (
                    newTeacherId
                ) {
                    const newTeacherResponse =
                        await api.get(
                            `/api/users/teacher-data/${newTeacherId}/`
                        );

                    newTeacher =
                        newTeacherResponse.data;
                }

                await fetchTeachers();

                if (
                    editingId
                ) {
                    closeModal();
                }

                await Swal.fire({
                    icon:
                        "success",

                    title:
                        "Teacher Transferred Successfully",

                    html: `
                        <div style="line-height:1.7;">
                            <p>
                                <strong>
                                    ${teacher.full_name}
                                </strong>
                                has been transferred to:
                            </p>

                            <p>
                                <strong>
                                    ${nextYear.name}
                                </strong>
                            </p>

                            <p>
                                The original
                                <strong>
                                    ${selectedYear.name}
                                </strong>
                                record remains preserved.
                            </p>

                            <p>
                                The new academic-year record is
                                <strong>
                                    ACTIVE
                                </strong>.
                            </p>
                        </div>
                    `,

                    confirmButtonColor:
                        "#ff7900",
                });

                if (
                    newTeacher
                ) {
                    await downloadTeacherPDF(
                        newTeacher
                    );
                }
            } catch (error) {
                console.error(
                    "TRANSFER TEACHER ERROR:",
                    error.response?.data ||
                        error
                );

                const backendError =
                    error.response?.data;

                let errorText =
                    "Unable to transfer teacher to the next academic year.";

                if (
                    backendError?.detail
                ) {
                    errorText =
                        backendError.detail;
                } else if (
                    backendError &&
                    typeof backendError ===
                        "object"
                ) {
                    errorText =
                        Object.entries(
                            backendError
                        )
                            .map(
                                (
                                    [
                                        field,
                                        errors,
                                    ]
                                ) =>
                                    `${field}: ${
                                        Array.isArray(
                                            errors
                                        )
                                            ? errors.join(
                                                  ", "
                                              )
                                            : errors
                                    }`
                            )
                            .join("\n");
                }

                await Swal.fire({
                    icon:
                        "error",

                    title:
                        "Transfer Failed",

                    text:
                        errorText,

                    confirmButtonColor:
                        "#111111",
                });
            } finally {
                setTransferLoading(
                    false
                );
            }
        };

    const handleDelete = async (
        id
    ) => {
        const result =
            await Swal.fire({
                title:
                    "Delete Teacher Record?",

                html: `
                    <p>
                        This will permanently delete
                        this academic-year teacher record.
                    </p>

                    <p>
                        <strong>
                            For employment termination,
                            use Removed or Sacked / Fired
                            instead.
                        </strong>
                    </p>

                    <p>
                        Historical records for other academic
                        years are not affected.
                    </p>
                `,

                icon:
                    "warning",

                showCancelButton:
                    true,

                confirmButtonColor:
                    "#d9363e",

                cancelButtonColor:
                    "#111111",

                confirmButtonText:
                    "Yes, Delete",
            });

        if (
            !result.isConfirmed
        ) {
            return;
        }

        try {
            await api.delete(
                `/api/users/teacher-data/${id}/`
            );

            await fetchTeachers();

            Swal.fire({
                icon:
                    "success",

                title:
                    "Deleted",

                text:
                    "Teacher record deleted successfully.",

                confirmButtonColor:
                    "#ff7900",
            });
        } catch (error) {
            console.error(
                "DELETE TEACHER ERROR:",
                error.response?.data ||
                    error
            );

            messageApi.error(
                error.response?.data
                    ?.detail ||
                    "Unable to delete teacher record."
            );
        }
    };

    const columns = [
        {
            title:
                "Teacher",

            key:
                "teacher",

            fixed:
                "left",

            width:
                250,

            render:
                (_, record) => (
                    <div
                        className="teacher-name-cell"
                    >
                        <div
                            className="teacher-avatar"
                        >
                            {record.full_name
                                ?.charAt(
                                    0
                                )
                                ?.toUpperCase()}
                        </div>

                        <div>
                            <Text strong>
                                {
                                    record.full_name
                                }
                            </Text>

                            <br />

                            <Text type="secondary">
                                {
                                    record.contact ||
                                    "No contact"
                                }
                            </Text>
                        </div>
                    </div>
                ),
        },

        {
            title:
                "Employment Status",

            key:
                "employment_status",

            width:
                190,

            render:
                (_, record) => {
                    const status =
                        getEmploymentStatus(
                            record
                        );

                    return (
                        <Space
                            direction="vertical"
                            size={4}
                        >
                            <Tag
                                color={getStatusTagColor(
                                    status
                                )}
                                style={{
                                    fontWeight:
                                        700,
                                }}
                            >
                                {getEmploymentStatusDisplay(
                                    record
                                )}
                            </Tag>

                            {record.academic_year_name && (
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            11,
                                    }}
                                >
                                    {
                                        record.academic_year_name
                                    }
                                </Text>
                            )}
                        </Space>
                    );
                },
        },

        {
            title:
                "Status Reason",

            key:
                "status_reason",

            width:
                280,

            render:
                (_, record) => {
                    if (
                        !record.status_reason
                    ) {
                        return (
                            <Text type="secondary">
                                —
                            </Text>
                        );
                    }

                    return (
                        <Text
                            ellipsis={{
                                tooltip:
                                    record.status_reason,
                            }}
                        >
                            {
                                record.status_reason
                            }
                        </Text>
                    );
                },
        },

        {
            title:
                "Qualification",

            dataIndex:
                "qualification",

            width:
                180,
        },

        {
            title:
                "Divisions",

            key:
                "divisions",

            width:
                260,

            render:
                (_, record) => {
                    const divisionIds =
                        Array.isArray(
                            record.divisions
                        )
                            ? record.divisions
                            : record.division
                                ? [
                                      record.division,
                                  ]
                                : [];

                    const names =
                        getDivisionNames(
                            divisionIds
                        );

                    if (
                        !names.length
                    ) {
                        return (
                            <Text type="secondary">
                                None
                            </Text>
                        );
                    }

                    return (
                        <Space
                            size={[
                                4,
                                4,
                            ]}
                            wrap
                        >
                            {names.map(
                                (
                                    name,
                                    index
                                ) => (
                                    <Tag
                                        key={
                                            `${name}-${index}`
                                        }
                                        color="orange"
                                    >
                                        {
                                            name
                                        }
                                    </Tag>
                                )
                            )}
                        </Space>
                    );
                },
        },

        {
            title:
                "Salary",

            dataIndex:
                "salary",

            width:
                130,

            render:
                (value) => (
                    <Text strong>
                        {
                            value ||
                            "0"
                        }
                    </Text>
                ),
        },

        {
            title:
                "Subjects",

            key:
                "subjects",

            width:
                260,

            render:
                (_, record) => (
                    <div
                        className="table-text"
                    >
                        {getSubjectNames(
                            record.subjects
                        ) || "None"}
                    </div>
                ),
        },

        {
            title:
                "Grade Classes",

            key:
                "grade_class",

            width:
                260,

            render:
                (_, record) => (
                    <div
                        className="table-text"
                    >
                        {getGradeNames(
                            record.grade_class
                        ) || "None"}
                    </div>
                ),
        },

        {
            title:
                "Actions",

            key:
                "actions",

            fixed:
                "right",

            width:
                280,

            render:
                (_, record) => (
                    <Space
                        wrap
                    >
                        <Button
                            className="edit-btn"
                            icon={
                                <EditOutlined />
                            }
                            onClick={() =>
                                handleEdit(
                                    record
                                )
                            }
                        >
                            Edit
                        </Button>

                        <Button
                            type="default"
                            icon={
                                <SwapOutlined />
                            }
                            loading={
                                transferLoading &&
                                editingId ===
                                    record.id
                            }
                            onClick={() =>
                                handleTransferToNextYear(
                                    record
                                )
                            }
                        >
                            Transfer
                        </Button>

                        <Button
                            danger
                            icon={
                                <DeleteOutlined />
                            }
                            onClick={() =>
                                handleDelete(
                                    record.id
                                )
                            }
                        />
                    </Space>
                ),
        },
    ];

    return (
        <>
            {contextHolder}

            <div
                className="teacher-page"
            >
                <div
                    className="teacher-page-header"
                >
                    <div>
                        <Title level={2}>
                            Teacher Management
                        </Title>

                        <Text>
                            Manage teachers,
                            qualifications,
                            assignments,
                            salary,
                            employment status,
                            and academic-year transfers.
                        </Text>
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        icon={
                            <PlusOutlined />
                        }
                        onClick={
                            openAddModal
                        }
                    >
                        Add Teacher
                    </Button>
                </div>

                <Card
                    className="teacher-control-card"
                >
                    <Row
                        gutter={[
                            16,
                            16,
                        ]}
                        align="middle"
                    >
                        <Col
                            xs={24}
                            sm={16}
                            md={10}
                            lg={8}
                        >
                            <Input
                                size="large"
                                prefix={
                                    <SearchOutlined />
                                }
                                placeholder="Search teacher by name, username or status..."
                                value={
                                    searchTerm
                                }
                                onChange={(e) => {
                                    setSearchTerm(
                                        e.target.value
                                    );

                                    setCurrentPage(
                                        1
                                    );
                                }}
                                allowClear
                            />
                        </Col>

                        <Col
                            xs={24}
                            sm={8}
                            md={14}
                            lg={16}
                            className="teacher-stats"
                        >
                            <Tag color="orange">
                                Total Teachers:{" "}
                                {
                                    teachers.length
                                }
                            </Tag>

                            <Tag color="black">
                                Academic Year:{" "}
                                {
                                    selectedYear?.name ||
                                    "Not Selected"
                                }
                            </Tag>

                            {selectedYear && (
                                <Tag color="blue">
                                    Status applies to this year
                                </Tag>
                            )}
                        </Col>
                    </Row>
                </Card>

                <Card
                    className="teacher-table-card"
                >
                    <Spin
                        spinning={
                            tableLoading
                        }
                    >
                        <Table
                            rowKey="id"
                            columns={
                                columns
                            }
                            dataSource={filteredTeachers.slice(
                                (
                                    currentPage -
                                    1
                                ) *
                                    pageSize,
                                currentPage *
                                    pageSize
                            )}
                            pagination={
                                false
                            }
                            scroll={{
                                x: 2100,
                            }}
                            bordered
                            size="middle"
                        />
                    </Spin>

                    <div
                        className="teacher-pagination"
                    >
                        <Pagination
                            current={
                                currentPage
                            }
                            pageSize={
                                pageSize
                            }
                            total={
                                filteredTeachers.length
                            }
                            showSizeChanger
                            pageSizeOptions={[
                                "8",
                                "16",
                                "32",
                                "64",
                            ]}
                            onChange={(
                                page,
                                size
                            ) => {
                                setCurrentPage(
                                    page
                                );

                                setPageSize(
                                    size
                                );
                            }}
                            showTotal={
                                (total) =>
                                    `Total ${total} teachers`
                            }
                        />
                    </div>
                </Card>

                <Modal
                    open={
                        open
                    }
                    title={
                        <div className="modal-title">
                            {
                                editingId
                                    ? "Edit Teacher"
                                    : "Add New Teacher"
                            }
                        </div>
                    }
                    onCancel={
                        closeModal
                    }
                    footer={null}
                    width={850}
                    centered
                    destroyOnClose
                >
                    <Form
                        form={
                            form
                        }
                        layout="vertical"
                        onFinish={
                            handleSubmit
                        }
                    >
                        <Row
                            gutter={[
                                16,
                                0,
                            ]}
                        >
                            <Col
                                xs={24}
                            >
                                <Form.Item
                                    label="Academic Year"
                                >
                                    <Input
                                        value={
                                            selectedYear?.name ||
                                            ""
                                        }
                                        disabled
                                        style={{
                                            fontWeight:
                                                700,
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Teacher Username"
                                    name="username"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Please select a username.",
                                        },
                                    ]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Select username"
                                        optionFilterProp="label"
                                        disabled={
                                            Boolean(
                                                editingId
                                            )
                                        }
                                        options={users.map(
                                            (
                                                user
                                            ) => ({
                                                value:
                                                    user.id,

                                                label:
                                                    user.username,
                                            })
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Full Name"
                                    name="full_name"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Enter full name.",
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="Teacher full name"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label={`Employment Status — ${
                                        selectedYear?.name ||
                                        "Academic Year"
                                    }`}
                                    name="employment_status"
                                    rules={[
                                        {
                                            required:
                                                true,

                                            message:
                                                "Please select employment status.",
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder="Select employment status"
                                        options={[
                                            {
                                                value:
                                                    "active",

                                                label:
                                                    "Active — Login Allowed",
                                            },
                                            {
                                                value:
                                                    "removed",

                                                label:
                                                    "Removed — Login Blocked",
                                            },
                                            {
                                                value:
                                                    "fired",

                                                label:
                                                    "Sacked / Fired — Login Blocked",
                                            },
                                        ]}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(
                                        prevValues,
                                        currentValues
                                    ) =>
                                        prevValues.employment_status !==
                                        currentValues.employment_status
                                    }
                                >
                                    {({
                                        getFieldValue,
                                    }) => {
                                        const status =
                                            getFieldValue(
                                                "employment_status"
                                            );

                                        const isBlocked =
                                            status ===
                                                "removed" ||
                                            status ===
                                                "fired";

                                        return (
                                            <Form.Item
                                                label={
                                                    isBlocked
                                                        ? "Status Reason — Required"
                                                        : "Status Reason"
                                                }
                                                name="status_reason"
                                                rules={
                                                    isBlocked
                                                        ? [
                                                              {
                                                                  required:
                                                                      true,

                                                                  whitespace:
                                                                      true,

                                                                  message:
                                                                      "Please provide the reason.",
                                                              },
                                                          ]
                                                        : []
                                                }
                                            >
                                                <Input.TextArea
                                                    rows={
                                                        3
                                                    }
                                                    placeholder={
                                                        isBlocked
                                                            ? "Explain why the teacher was removed or sacked/fired..."
                                                            : "Optional status note..."
                                                    }
                                                    maxLength={
                                                        2000
                                                    }
                                                    showCount
                                                />
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Qualification"
                                    name="qualification"
                                >
                                    <Input
                                        placeholder="e.g. B.Ed, B.Sc"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Assign Divisions"
                                    name="divisions"
                                >
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        placeholder="Select divisions"
                                        optionFilterProp="label"
                                        options={divisions.map(
                                            (
                                                division
                                            ) => ({
                                                value:
                                                    division.id,

                                                label:
                                                    division.name,
                                            })
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Contact"
                                    name="contact"
                                >
                                    <Input
                                        placeholder="Contact number"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Salary"
                                    name="salary"
                                >
                                    <InputNumber
                                        style={{
                                            width:
                                                "100%",
                                        }}
                                        placeholder="Salary"
                                        min={
                                            0
                                        }
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Date Added"
                                    name="date_add"
                                >
                                    <Input
                                        type="date"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                                md={12}
                            >
                                <Form.Item
                                    label="Dependent Student"
                                    name="dependent_student"
                                >
                                    <Input
                                        placeholder="Dependent student"
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                            >
                                <Form.Item
                                    label="Assign Subjects"
                                    name="subjects"
                                >
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        placeholder="Select subjects"
                                        optionFilterProp="label"
                                        options={subjects.map(
                                            (
                                                subject
                                            ) => ({
                                                value:
                                                    subject.id,

                                                label:
                                                    subject.name,
                                            })
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            <Col
                                xs={24}
                            >
                                <Form.Item
                                    label="Assign Grade Classes"
                                    name="grade_class"
                                >
                                    <Select
                                        mode="multiple"
                                        allowClear
                                        showSearch
                                        placeholder="Select grade classes"
                                        optionFilterProp="label"
                                        options={gradeClasses.map(
                                            (
                                                grade
                                            ) => ({
                                                value:
                                                    grade.id,

                                                label:
                                                    grade.name,
                                            })
                                        )}
                                    />
                                </Form.Item>
                            </Col>

                            {editingId && (
                                <>
                                    <Col
                                        xs={24}
                                    >
                                        <Card
                                            size="small"
                                            style={{
                                                background:
                                                    "#f8fafc",

                                                border:
                                                    "1px solid #e5e7eb",
                                            }}
                                        >
                                            <Space
                                                direction="vertical"
                                                size={2}
                                            >
                                                <Text strong>
                                                    Employment Status Audit
                                                </Text>

                                                <Text type="secondary">
                                                    Status:{" "}
                                                    {
                                                        getEmploymentStatusDisplay(
                                                            editingTeacher
                                                        )
                                                    }
                                                </Text>

                                                {editingTeacher?.status_changed_at && (
                                                    <Text type="secondary">
                                                        Changed:{" "}
                                                        {new Date(
                                                            editingTeacher.status_changed_at
                                                        ).toLocaleString()}
                                                    </Text>
                                                )}

                                                {editingTeacher?.status_changed_by && (
                                                    <Text type="secondary">
                                                        Changed by:{" "}
                                                        {
                                                            typeof editingTeacher.status_changed_by ===
                                                            "object"
                                                                ? (
                                                                      editingTeacher
                                                                          .status_changed_by
                                                                          .username ||
                                                                      editingTeacher
                                                                          .status_changed_by
                                                                          .email ||
                                                                      "Administrator"
                                                                  )
                                                                : editingTeacher.status_changed_by
                                                        }
                                                    </Text>
                                                )}
                                            </Space>
                                        </Card>
                                    </Col>

                                    <Col
                                        xs={24}
                                    >
                                        <Card
                                            size="small"
                                            style={{
                                                marginTop:
                                                    4,

                                                background:
                                                    "#fff7ed",

                                                border:
                                                    "1px solid #fed7aa",
                                            }}
                                        >
                                            <Space
                                                direction="vertical"
                                                size={4}
                                                style={{
                                                    width:
                                                        "100%",
                                                }}
                                            >
                                                <Text
                                                    strong
                                                    style={{
                                                        color:
                                                            "#c2410c",
                                                    }}
                                                >
                                                    Academic Year Transfer
                                                </Text>

                                                <Text type="secondary">
                                                    Transfer this teacher to the next academic year without changing the current{" "}
                                                    <strong>
                                                        {
                                                            selectedYear?.name
                                                        }
                                                    </strong>{" "}
                                                    record.
                                                </Text>

                                                <Button
                                                    icon={
                                                        <SwapOutlined />
                                                    }
                                                    loading={
                                                        transferLoading
                                                    }
                                                    disabled={
                                                        getEmploymentStatus(
                                                            editingTeacher
                                                        ) !==
                                                        "active"
                                                    }
                                                    onClick={() =>
                                                        handleTransferToNextYear(
                                                            editingTeacher
                                                        )
                                                    }
                                                >
                                                    Transfer to Next Academic Year
                                                </Button>
                                            </Space>
                                        </Card>
                                    </Col>
                                </>
                            )}
                        </Row>

                        <div
                            className="modal-actions"
                        >
                            <Button
                                onClick={
                                    closeModal
                                }
                            >
                                Cancel
                            </Button>

                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={
                                    loading
                                }
                            >
                                {
                                    editingId
                                        ? "Update Teacher"
                                        : "Save Teacher"
                                }
                            </Button>
                        </div>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default TeacherDataForm;
